<script setup lang="ts">
/**
 * OtherSuggestions Component
 *
 * Description: A reusable Vue component designed to display a list of other suggestions.
 *
 * Props:
 *   - data: An array of objects containing the following properties:
 *     - video: A string representing the video URL.
 *     - bg: A string representing the background image URL.
 *     - text: A string representing the suggestion text.
 *
 * Data:
 *   - active: A boolean indicating whether the component is active.
 *   - hoverStates: An array of booleans indicating the hover state of each suggestion card.
 *
 * Methods:
 *   - handleMouseEnter(index: number): A function that handles the mouse enter index on a suggestion card.
 *   - handleMouseLeave(index: number): A function that handles the mouse leave index on a suggestion card.
 *
 * Template:
 *   - The component template consists of a list of suggestion cards, each containing a video, background image, and text.
 *
 * Styles:
 *   - The component uses CSS styles to layout the suggestion cards and adapt to different screen sizes.
 *
 * Watchers:
 *   - Watches the `data` prop for changes and updates the component accordingly.
 *   - Watches the `active` data property and updates the component's styles accordingly.
 *
 * Lifecycle Hooks:
 *   - mounted: Initializes the component and sets up event listeners.
 */
import { UserAnimationEnabled } from "../../../states/animation";
const { animationEnabled } = useCustom();
const animation = UserAnimationEnabled();
const { data } = defineProps<{
  data: {
    video: string;
    bg: string;
    text: string;
    url: string;
    out: boolean;
  }[];
}>();
const isPlaying = ref<boolean[]>(data.map(() => false));
const BaseUrl = import.meta.env.VITE_OLD_BASE_URL;

/**
 * Activates playing state for the video at the specified index when mouse enters.
 *
 * @param {number} index - The index of the suggestion card whose video should start playing.
 *
 * Description:
 * - This function is triggered when the mouse enters a suggestion card.
 * - It checks if animations are enabled before setting the playing state to true.
 */

/**
 * Activates playing state for the video at the specified index when mouse enters.
 *
 * @param {number} index - The index of the suggestion card whose video should start playing.
 *
 * Description:
 * - This function is triggered when the mouse enters a suggestion card.
 * - It checks if animations are enabled before setting the playing state to true.
 */
const handleMouseEnter = (index: number) => {
  if (!animation.value) return;
  isPlaying.value[index] = true;
};

/**
 * Deactivates playing state for the video at the specified index when mouse leaves.
 *
 * @param {number} index - The index of the suggestion card whose video should stop playing.
 *
 * Description:
 * - This function is triggered when the mouse leaves a suggestion card.
 * - It sets the playing state to false for the video at the given index.
 */

const handleMouseLeave = (index: number) => {
  isPlaying.value[index] = false;
};
/**
 * Redirects the user to a specified URL.
 *
 * @param {string} url - The URL to redirect the user to.
 * @param {boolean} out - A flag indicating whether the redirect should open an external URL.
 *
 * Description:
 * - If the `out` parameter is true, the function concatenates the `BaseUrl` with the
 *   provided `url` and sets `window.location.href` to redirect the user to an external page.
 * - If `out` is false, the function uses `navigateTo` to perform an internal navigation
 *   within the application.
 */

const redirectToUrl = (url: string, out: boolean) => {
  if (out) {
    window.location.href = BaseUrl + "" + url;
  } else {
    navigateTo(url);
  }
};
onMounted(() => {
  animation.value = animationEnabled;
  const isMobile = window.matchMedia("(max-width: 780px)").matches;
  if (isMobile && animation.value) {
    isPlaying.value = data.map(() => true);
  }
});
</script>
<template>
  <section class="other-suggestions">
    <h2 class="title">
      {{ $t("UiServiceDetail.otherSuggestions.title.part-1") }}
      <br class="mobile-break" />
      {{ $t("UiServiceDetail.otherSuggestions.title.part-2") }}
    </h2>
    <div class="cards cursor-hover">
      <div
        v-for="(card, index) in data"
        :key="index"
        class="card"
        @mouseenter="handleMouseEnter(index)"
        @mouseleave="handleMouseLeave(index)"
        @click="redirectToUrl(card.url, card.out)"
      >
        <div class="shadow"></div>
        <div class="bg"></div>
        <CommonVideoWithPlaceholder
          :video-src="card.video"
          :placeholder="card.bg"
          :class-video="'img video hover-video'"
          :class-placeholder="'img'"
          :is-playing="isPlaying[index]"
          :alt="card.text"
        />
        <p class="text">{{ card.text }}</p>
      </div>
    </div>
  </section>
</template>
<style scoped>
.title {
  text-align: center;
  color: var(--vt-c-medium-purple);
  font: normal normal normal 48px/67px "Gilmer Bold";
}
.text {
  text-align: center;
  display: block;
  font: normal normal normal 48px/67px "Gilmer Heavy";
  margin-bottom: 1rem;
  color: var(--vt-c-white);
  -webkit-text-stroke: 1px var(--vt-c-purple);
  opacity: 1;
  z-index: 2;
}

.cards {
  /* padding: 0 4rem; */
  display: flex;
  padding-left: 3.8rem;
  padding-right: 3.8rem;
  padding-bottom: 5rem;
  grid-template-columns: repeat(3, 1fr);
  justify-content: center;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
}
::v-deep(.img) {
  position: absolute;
  z-index: -1;
  height: 100%;
  object-fit: contain;
  border-radius: 27px;
}
.bg {
  position: absolute;
  width: 100%;
  height: 100%;
  background: #a78ded2f 0% 0% no-repeat padding-box;
  border-radius: 27px;
  opacity: 1;
  transition: all 0.3s ease;
}
.card {
  overflow: hidden;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: end;
  /* background: var(--vt-c-light-purple)2f 0% 0% no-repeat padding-box; */
  border: 1px solid var(--vt-c-purple);
  border-radius: 27px;
  width: 447px;
  height: 280px;
  transition: all 0.3s ease;
}
.shadow {
  position: absolute;
  width: 100%;
  height: 100%;
  background: transparent;
  transition: all 0.3s ease;
}
.card:hover .bg {
  background: none;
}
.card:hover .shadow {
  border-radius: 27px;
  border: none;

  box-shadow:
    inset 0 0 12px #7448f1,
    0 0 18px #7448f0;
}
@media screen and (max-width: 1200px) {
  .cards {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}
@media screen and (max-width: 780px) {
  .card {
    width: 362px;
    height: 190px;
  }
  .cards {
    gap: 1rem;
  }
  .text {
    font-size: 35px;
    line-height: 40px;
  }
  .title {
    font-size: 25px;
    line-height: 31px;
  }
}
</style>
